import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { IconExternalLink } from '@tabler/icons'
import { Grid } from '@mantine/core'
import useStyles from './FiveMistakesBusinessesMake.styles'
import { translate } from '../../../../utilities/language'
import { getAttribution } from '../../../../utilities/content'
import {
  ARTICLE_IMAGE_AVIF,
  ARTICLE_IMAGE_JPEG,
  FIVE_MISTAKES_TITLE_HTML,
  FIVE_MISTAKES_TITLE_HTML_ES,
  FIVE_MISTAKES_TITLE_HTML_FR,
  FIVE_MISTAKES_TITLE_HTML_KO,
  FIVE_MISTAKES_ARTICLE_PUBLISHED_STR,
} from '../../../../static-data/five-mistakes-businesses-make-data'
import {
  MAIN_SITE_ENVIRONMENT_HOST,
  MAIN_SITE_ENVIRONMENT_HOST_ES,
  MAIN_SITE_ENVIRONMENT_HOST_FR,
  MAIN_SITE_ENVIRONMENT_HOST_KO,
} from '../../../../utilities/config'

const BackToArticle: React.ComponentType<any> = dynamic(
  () => import('../../../controls/BackToArticle'),
  {
    ssr: true,
  }
)
const ReadyToTransform: React.ComponentType<any> = dynamic(
  () => import('../../../controls/ReadyToTransform'),
  {
    ssr: true,
  }
)

export default function FiveMistakesBusinessesMake(props: any) {
  const { classes } = useStyles()
  const selectedLanguage: any = props?.selectedLanguage
  const width: any = props?.width
  const avifCompatible = props?.avifCompatible

  const articleImage = useMemo(() => {
    return (
      <Image
        width={512}
        height={279}
        sizes="(max-width: 350px) 256px, 
               (max-width: 430px) 300px, 
               (max-width: 590px) 384px, 
               512px"
        src={avifCompatible ? ARTICLE_IMAGE_AVIF : ARTICLE_IMAGE_JPEG}
        alt=""
        priority={true}
        style={{
          marginTop: '30px',
          borderRadius: '10px',
          display: 'block',
          width: '100%',
          maxWidth: '512px',
          height: 'auto',
        }}
      />
    )
  }, [avifCompatible])

  return (
    <div className={classes.mainGrid}>
      <div className={classes.innerDiv}>
        <Grid columns={24}>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <h1 className={classes.standardText + ' ' + classes.h1Tag}>
                {translate(
                  FIVE_MISTAKES_TITLE_HTML,
                  FIVE_MISTAKES_TITLE_HTML_ES,
                  FIVE_MISTAKES_TITLE_HTML_FR,
                  FIVE_MISTAKES_TITLE_HTML_KO,
                  selectedLanguage
                )}
              </h1>
              {getAttribution(
                translate(
                  'Editor',
                  'Editora',
                  'Éditrice',
                  '편집자',
                  selectedLanguage
                ),
                FIVE_MISTAKES_ARTICLE_PUBLISHED_STR,
                selectedLanguage,
                false
              )}
              {articleImage}
            </div>
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  <span className={classes.spanItalic}>
                    <span className={classes.spanBolder}>Just imagine:</span>
                  </span>{' '}
                  a fancy Italian trattoria, white tablecloths, $40 pasta
                  dishes, and candlelit ambiance. The cuisine is excellent, the
                  service impeccable. Then, heavy metal begins to play on the
                  speakers.
                </>,
                <>
                  Imagínese: una elegante trattoria italiana, manteles blancos,
                  platos de pasta de 40 dólares y un ambiente a la luz de las
                  velas. La comida es excelente, el servicio impecable. Y de
                  repente, empieza a sonar heavy metal por los altavoces.
                </>,
                <>
                  Imaginez&nbsp;: une trattoria italienne chic, des nappes
                  blanches, des plats de pâtes à 40 dollars et une ambiance aux
                  chandelles. La cuisine est excellente, le service impeccable.
                  Puis, de la musique heavy metal commence à retentir dans les
                  haut-parleurs.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    상상해
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    보세요. 고급스러운
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이탈리아
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    레스토랑에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    하얀
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    테이블보,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    40달러짜리
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    파스타
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    요리,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    그리고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    촛불이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    켜진
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분위기.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음식은
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    훌륭하고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    서비스는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    더할
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    나위
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    없이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    완벽합니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    그런데
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    갑자기
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    스피커에서
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    헤비메탈
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    흘러나옵니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  That is not a hypothetical situation. It is a real mistake
                  that made a restaurant lose repeat clients and stellar
                  reviews. When it comes to{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST}
                    title={
                      'Professional Background Music Streaming for Commercial Locations'
                    }
                    rel="noopener noreferrer"
                  >
                    <span>
                      music for&nbsp;
                      <span className={classes.spanPre}>
                        business&nbsp;
                        <IconExternalLink />
                      </span>
                    </span>
                  </a>{' '}
                  it is not what you serve but what you play. However, the vast
                  majority of businesses commit critical mistakes, costing them
                  clients, income, and, in certain instances, tens of thousands
                  in legal fines.
                </>,
                <>
                  No se trata de una situación hipotética. Fue un error real que
                  provocó que un restaurante perdiera clientes habituales y
                  excelentes reseñas. En lo que respecta a la{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST_ES}
                    title={
                      'Servicio de transmisión de música de fondo profesional para locales comerciales'
                    }
                    rel="noopener noreferrer"
                  >
                    <span>
                      música en los&nbsp;
                      <span className={classes.spanPre}>
                        negocios&nbsp;
                        <IconExternalLink />
                      </span>
                    </span>
                  </a>
                  , no importa tanto lo que se sirve, sino lo que se reproduce.
                  Sin embargo, la gran mayoría de las empresas cometen errores
                  graves que les cuestan clientes, ingresos y, en algunos casos,
                  decenas de miles de dólares en multas legales.
                </>,
                <>
                  Il ne s'agit pas d'une situation hypothétique. C'est une
                  erreur bien réelle qui a fait perdre à un restaurant des
                  clients fidèles et d'excellentes critiques. En matière de{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST_FR}
                    title={
                      "Diffusion de musique d'ambiance professionnelle pour les établissements commerciaux."
                    }
                    rel="noopener noreferrer"
                  >
                    <span>
                      musique d'ambiance pour les&nbsp;
                      <span className={classes.spanPre}>
                        commerces&nbsp;
                        <IconExternalLink />
                      </span>
                    </span>
                  </a>{' '}
                  ce n'est pas ce que l'on sert qui compte, mais la musique que
                  l'on diffuse. Pourtant, la grande majorité des entreprises
                  commettent des erreurs cruciales qui leur coûtent des clients,
                  des revenus et, dans certains cas, des dizaines de milliers
                  d'euros d'amendes.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이것은
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    가상적인
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    상황이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    아닙니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    실제로
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    레스토랑이
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    단골
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    고객과
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    훌륭한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    평판을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    잃게
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    만든
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    실례입니다.
                  </span>{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={MAIN_SITE_ENVIRONMENT_HOST_ES}
                    title={
                      'Servicio de transmisión de música de fondo profesional para locales comerciales'
                    }
                    rel="noopener noreferrer"
                  >
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사업장에서
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악을
                    </span>
                    &nbsp;
                    <span className={classes.spanPre}>
                      틀&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사업장에서
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    틀
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    때는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    어떤
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음식을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    제공하느냐가
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    아니라
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    어떤
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    음악을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    트느냐가
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    중요합니다.
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    하지만
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    대다수의
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    사업체들은
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    치명적인
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    실수를
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    저지르고
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있으며,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이로
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    인해
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    고객과
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    수익을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    잃고,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    심지어는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    수만
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    달러에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    달하는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    법적
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    벌금을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    물기도
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    합니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Let’s analyze the five most expensive business playlist
                  mistakes and see how they can be fixed before they damage your
                  bottom line.
                </>,
                <>
                  Analicemos los cinco errores más costosos al crear listas de
                  reproducción para negocios y veamos cómo solucionarlos antes
                  de que afecten negativamente a sus resultados económicos.
                </>,
                <>
                  Analysons les cinq erreurs les plus coûteuses en matière de
                  listes de lecture pour les entreprises et voyons comment les
                  corriger avant qu'elles n'affectent vos résultats financiers.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    가장
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    흔하게
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    발생하는
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    다섯
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    가지
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    비즈니스
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    플레이리스트
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    관련
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    실수를
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    분석하고,
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    이러한
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    실수가
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    수익에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    악영향을
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    미치기
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    전에
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    어떻게
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    해결할
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    수
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    있는지
                  </span>{' '}
                  <span className={classes.asianTextBlock} aria-hidden={true}>
                    알아보겠습니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <section id={'mistake-1-using-consumer-services-illegally'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>
                    Mistake #1: Using Consumer Streaming Services&nbsp;Illegally
                  </>,
                  <>
                    Error 1: Utilizar servicios de streaming para consumidores
                    de forma ilegal.
                  </>,
                  <>
                    Erreur 1&nbsp;: Utiliser les services de streaming pour les
                    consommateurs de forme illégale.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      #1:
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소비자용
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      스트리밍
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      서비스를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      불법적으로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      이용하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id={'mistake-1-problem-defined'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>The Problem</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>El problema</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>Le problème</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문제점
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      The most common and expensive mistake in choosing music
                      for business is assuming your Spotify Premium or Apple
                      Music subscription includes commercial use.
                    </>,
                    <>
                      El error más común y costoso al elegir música para un
                      negocio es asumir que la suscripción a Spotify Premium o
                      Apple Music incluye el uso comercial.
                    </>,
                    <>
                      L'erreur la plus courante et la plus coûteuse lors du
                      choix de musique pour une entreprise est de croire que
                      votre abonnement Spotify Premium ou Apple Music inclut
                      l'utilisation commerciale.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사업장에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사용할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선택할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        때
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        가장
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        흔하고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        비용이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        많이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        드는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실수는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Spotify
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Premium이나
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Apple
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Music
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        구독에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        상업적
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사용
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        권한이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        포함되어
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있다고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        생각하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>It doesn’t.</>,
                    <>No es así.</>,
                    <>Ce n'est pas le cas.</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        그렇지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        않습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Spotify’s terms explicitly state: “You promise and agree
                      that you are using the Spotify Service and Content for
                      your own personal, non-commercial use.” Playing consumer
                      streaming in your business is against the terms of service
                      and copyright law.
                    </>,
                    <>
                      Los términos y condiciones de Spotify establecen
                      explícitamente: «Usted promete y acepta que está
                      utilizando el servicio y el contenido de Spotify para su
                      uso personal y no comercial». Reproducir música en
                      streaming para consumidores en su negocio incumple los
                      términos del servicio y la ley de derechos de autor.
                    </>,
                    <>
                      Les conditions d'utilisation de Spotify stipulent
                      explicitement&nbsp;: «&nbsp;Vous promettez et acceptez
                      d'utiliser le service et le contenu de Spotify à des fins
                      personnelles et non commerciales.&nbsp;» Diffuser de la
                      musique en streaming destinée aux particuliers dans votre
                      entreprise est contraire aux conditions d'utilisation et à
                      la loi sur le droit d'auteur.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스포티파이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이용
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        ​​약관에는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        다음과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        같이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        명시되어
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        "귀하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        스포티파이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        및
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        콘텐츠를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        개인적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        비상업적
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        용도로만
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사용해야
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        함에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        동의합니다."
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사업장에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        개인용
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        스트리밍
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사용하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        약관
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        및
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        저작권법
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        위반입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id={'mistake-1-legal-risks'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        The Legal Risks:
                      </span>
                    </>,
                    <>Los riesgos legales:</>,
                    <>Les risques juridiques&nbsp;:</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        법적
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        위험:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Minimum fine: $750 per song</>,
                      <>Multa mínima: 750 dólares por canción</>,
                      <>Amende minimale&nbsp;: 750 dollars par chanson</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          최소
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          벌금:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          곡당
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          750달러
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Standard range: $750-$30,000 per song</>,
                      <>
                        Rango de precios estándar: entre 750 y 30.000 dólares
                        por canción
                      </>,
                      <>
                        Gamme de prix standard&nbsp;: de 750 $ à 30 000 $ par
                        chanson
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Standard
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          range:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          $750-$30,000
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          per
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          song
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Willful infringement: Up to $150,000 per song</>,
                      <>
                        Infracción intencionada: Hasta 150.000 dólares por
                        canción
                      </>,
                      <>
                        Contrefaçon intentionnelle&nbsp;: jusqu'à 150 000
                        dollars par chanson
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고의적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          저작권
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          침해:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          곡당
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          최대
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          15만
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          달러의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          벌금
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Plus: Court costs, attorney fees, settlement expenses
                      </>,
                      <>
                        Además: costas judiciales, honorarios de abogados,
                        gastos de conciliación
                      </>,
                      <>
                        De plus&nbsp;: frais de justice, honoraires d'avocat,
                        frais de règlement à l'amiable
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          추가적으로:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소송
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          비용,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          변호사
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수임료,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          합의금
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          등이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          발생할
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id={'mistake-1-real-world-examples'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Real Cases:</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>Casos reales:</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Cas réels&nbsp;:
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        실제
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사례:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        XY Bar, Wichita
                      </span>
                      &nbsp;(2019): BMI sued for streaming five unlicensed
                      songs. The suit was a last resort in an effort to recover
                      unspecified damages following several attempts to contact.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        XY Bar, Wichita
                      </span>
                      &nbsp;(2019): BMI presentó una demanda por la transmisión
                      de cinco canciones sin licencia. La demanda fue el último
                      recurso para intentar recuperar una indemnización por
                      daños y perjuicios de cuantía indeterminada, tras varios
                      intentos de contacto infructuosos.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        XY Bar, Wichita
                      </span>
                      &nbsp; (2019)&nbsp;: BMI a intenté une action en justice
                      pour la diffusion de cinq chansons sans licence. Cette
                      action en justice était un dernier recours pour obtenir
                      des dommages et intérêts d'un montant non précisé, après
                      plusieurs tentatives de contact infructueuses.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위치타의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        XY
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        바
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        (2019년):
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        BMI는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        허가
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        없이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        다섯
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        곡의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        스트리밍한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        혐의로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소송을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        제기했습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이번
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소송은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        여러
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        차례
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        연락을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        시도했음에도
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        불구하고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        해결되지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        않아
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        결국
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        손해
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        배상을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        청구하기
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        위한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        최후의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        수단으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        진행되었습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <span className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Washington Business
                      </span>
                      &nbsp;(2017): The ASCAP lawsuit sought $3,000-$120,000 for
                      just four songs. A private investigator documented songs
                      played without the owner’s knowledge.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Washington Business
                      </span>
                      &nbsp;(2017): La demanda de ASCAP solicitaba entre 3000 y
                      120 000 dólares por tan solo cuatro canciones. Un
                      investigador privado documentó la reproducción de
                      canciones sin el conocimiento del propietario. La demanda
                      se presentó como último recurso para intentar recuperar
                      daños y perjuicios no especificados, tras varios intentos
                      infructuosos de establecer contacto.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Washington Business
                      </span>
                      &nbsp;(2017)&nbsp;: Le procès intenté par l'ASCAP
                      réclamait entre 3&nbsp;000 et 120&nbsp;000 dollars pour
                      seulement quatre chansons. Un détective privé a documenté
                      les chansons diffusées sans l'autorisation du
                      propriétaire.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        워싱턴
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        비즈니스(2017):
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        ASCAP
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소송은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        단
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        네
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        곡에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        대해
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        3,000달러에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        12만
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        달러에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        달하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        손해배상을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        요구했습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사설
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        탐정은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        저작권
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소유자의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        허락
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        없이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        연주된
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        곡들을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기록으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        남겼습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </span>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Several corporations unknowingly use Spotify, which
                      deprives the music industry of an annual loss of $2
                      billion in royalties.
                    </>,
                    <>
                      Varias empresas utilizan Spotify sin saberlo, lo que priva
                      a la industria musical de unos ingresos anuales de 2.000
                      millones de dólares en concepto de derechos de autor.
                    </>,
                    <>
                      Plusieurs entreprises utilisent Spotify sans le savoir, ce
                      qui prive l'industrie musicale de 2 milliards de dollars
                      de redevances par an.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        여러
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기업들이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        자신도
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        모르게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        스포티파이를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이용하고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있으며,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        인해
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        산업은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        매년
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        20억
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        달러에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        달하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        로열티
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        손실을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        입고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id={'mistake-1-how-to-fix'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>How to Fix</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Cómo solucionarlo
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Comment réparer
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        해결
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방법
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Commercial music selection
                      </span>{' '}
                      needs licensed services designed to be used in businesses:
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        La selección de música
                      </span>{' '}
                      para fines comerciales requiere servicios con licencia
                      diseñados para ser utilizados en negocios:
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        La sélection de musique commerciale
                      </span>{' '}
                      nécessite des services sous licence conçus pour être
                      utilisés dans les entreprises&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상업용
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선택할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        때는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사업장에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사용하도록
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        설계된
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        라이선스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이용해야
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <section id={'mistake-1-providers-that-fix'}>
                  <h3 className={classes.pParagraph}>
                    {translate(
                      <>Provider Comparison Table</>,
                      <>Tabla comparativa de proveedores</>,
                      <>Tableau comparatif des fournisseurs</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          제공업체
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          비교표
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h3>
                  <div className={classes.dataTableWrapper}>
                    <table className={classes.dataTable}>
                      <caption className={classes.dtCaption}>
                        {translate(
                          <>
                            Price and Features Comparison of Business Background
                            Music Providers
                          </>,
                          <>
                            Comparación de precios y características de
                            proveedores de música de fondo para empresas
                          </>,
                          <>
                            Comparaison des prix et des fonctionnalités des
                            fournisseurs de musique d'ambiance pour les
                            entreprises
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              기업용 배경 음악 제공업체의 가격 및 기능을
                              비교합니다.
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col" className={classes.dataTableHeader}>
                            {translate(
                              <>Service</>,
                              <>Servicio</>,
                              <>Service</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  서비스
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </th>
                          <th scope="col" className={classes.dataTableHeader}>
                            {translate(
                              <>Monthly&nbsp;Cost</>,
                              <>Cuota&nbsp;Mensual</>,
                              <>Frais&nbsp;Mensuels</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  월별
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  비용
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </th>
                          <th scope="col" className={classes.dataTableHeader}>
                            {translate(
                              <>What&nbsp;You&nbsp;Get</>,
                              <>Lo&nbsp;que&nbsp;Obtienes:</>,
                              <>Ce&nbsp;que&nbsp;Vous&nbsp;Obtenez</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  당신이
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  얻는
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  것
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className={classes.dataTableField}>
                            Soundtrack&nbsp;Your&nbsp;Brand
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>$25-50&nbsp;per&nbsp;zone</>,
                              <>$25-50&nbsp;por&nbsp;zona</>,
                              <>25-50&nbsp;$&nbsp;par&nbsp;zone</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  $25-50
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>90M+ songs, full PRO coverage, no ads, more</>,
                              <>
                                Más de 90 millones de canciones, cobertura PRO
                                completa, sin anuncios y mucho más
                              </>,
                              <>
                                Plus de 90 millions de chansons, couverture PRO
                                complète, sans publicité, et bien plus encore
                              </>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  9천만
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  곡
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  이상의
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  노래,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  완벽한
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  PRO
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  서비스,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  광고
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  없음,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  그리고
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  더
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  많은
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  혜택
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  제공
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.dataTableField}>
                            <a
                              className={classes.aHref}
                              target="_blank"
                              href={translate(
                                MAIN_SITE_ENVIRONMENT_HOST,
                                MAIN_SITE_ENVIRONMENT_HOST_ES,
                                MAIN_SITE_ENVIRONMENT_HOST_FR,
                                MAIN_SITE_ENVIRONMENT_HOST_KO,
                                selectedLanguage
                              )}
                              title={translate(
                                'The least expensive, full-featured service for licensed commercial background music.',
                                'Este es el servicio más asequible y a la vez más completo entre los proveedores de servicios de música ambiental comercial con licencia.',
                                "Il s'agit du service le plus abordable et pourtant le plus complet parmi les fournisseurs de services de musique d'ambiance commerciale sous licence.",
                                '라이선스가 부여된 상업용 배경 음악 서비스를 제공하는 업체 중 가장 저렴하면서도 모든 기능을 갖춘 서비스입니다.',
                                selectedLanguage
                              )}
                              rel="noopener noreferrer"
                            >
                              <span translate="no">
                                Practical&nbsp;
                                <span className={classes.spanPre}>
                                  Stream&nbsp;
                                  <IconExternalLink
                                    size={
                                      width < 500 ? 17 : width < 920 ? 20 : 24
                                    }
                                    width={
                                      width < 500 ? 17 : width < 920 ? 20 : 24
                                    }
                                    height={
                                      width < 500 ? 17 : width < 920 ? 20 : 24
                                    }
                                  />
                                </span>
                              </span>
                            </a>{' '}
                          </td>
                          <td className={classes.dataTableField}>
                            <span className={classes.spanGood}>
                              {translate(
                                <>$8.34</>,
                                <>$8.34</>,
                                <>8,34&nbsp;$</>,
                                <>
                                  <span
                                    aria-hidden={true}
                                    className={classes.asianTextBlock}
                                  >
                                    $8.34
                                  </span>
                                </>,
                                selectedLanguage
                              )}
                            </span>
                          </td>
                          <td className={classes.dataTableField}>
                            <span className={classes.spanGood}>
                              {translate(
                                <>
                                  Licensed commercial music, curated business
                                  playlists, multi-location control, more
                                </>,
                                <>
                                  Música comercial con licencia, listas de
                                  reproducción personalizadas para negocios,
                                  control en múltiples ubicaciones y mucho más
                                </>,
                                <>
                                  Musique commerciale sous licence, listes de
                                  lecture personnalisées pour les entreprises,
                                  gestion multi-sites, et bien plus encore.
                                </>,
                                <>
                                  <span
                                    aria-hidden={true}
                                    className={classes.asianTextBlock}
                                  >
                                    라이선스된
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    상업용
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    음악,
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    엄선된
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    비즈니스
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    플레이리스트,
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    다중
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    매장
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    관리
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    기능
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    등
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    다양한
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    기능
                                  </span>{' '}
                                  <span
                                    className={classes.asianTextBlock}
                                    aria-hidden={true}
                                  >
                                    제공
                                  </span>
                                </>,
                                selectedLanguage
                              )}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.dataTableField}>
                            Cloud&nbsp;Cover&nbsp;Music
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>$17-35</>,
                              <>$17-35</>,
                              <>17-35&nbsp;$</>,
                              <>$17-35</>,
                              selectedLanguage
                            )}
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>Scheduling, compliance docs, more</>,
                              <>
                                Programación, documentos de cumplimiento, y más
                              </>,
                              <>Planification, documents de conformité, etc.</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                ></span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.dataTableField}>
                            SoundMachine
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>$27-36</>,
                              <>$27-36</>,
                              <>27-36&nbsp;$</>,
                              <>$27-36</>,
                              selectedLanguage
                            )}
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>61M+ tracks, SESAC/GMR coverage, more</>,
                              <>
                                Más de 61 millones de canciones, cobertura de
                                SESAC/GMR, y mucho más
                              </>,
                              <>
                                Plus de 61 millions de chansons, couverture de
                                SESAC/GMR, et bien plus
                              </>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  6,100만
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  곡
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  이상의
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  음원,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  SESAC/GMR
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  저작권
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  관리,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  그리고
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  더
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  많은
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  기능
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  제공
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                          <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                          <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>
              </section>
              <section id={'mistake-1-avoiding-copyright-errors'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        To avoid copyright mistakes:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Para evitar errores de derechos de autor:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Pour éviter les erreurs de droits d'auteur&nbsp;:
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        저작권
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        관련
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실수를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        피하려면
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        다음
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사항을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        준수하세요.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Use only commercially licensed services</>,
                      <>Utilice únicamente servicios con licencia comercial</>,
                      <>
                        Utilisez uniquement des services sous licence
                        commerciale
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          상업적으로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          허가된
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          서비스만
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용하십시오.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Document your licensing for audits</>,
                      <>Documente sus licencias para las auditorías</>,
                      <>Documentez vos licences en vue des audits</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          감사를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위해
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          라이선스
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          정보를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          문서화하십시오.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Never use consumer streaming services</>,
                      <>
                        Nunca utilices servicios de streaming para consumidores
                      </>,
                      <>
                        N'utilisez jamais de services de streaming grand public
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          소비자용
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          스트리밍
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          서비스를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          절대
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사용하지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          마세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The cost difference? $11/month (illegal) vs. $25–60/month
                    (legal and secured). The choice is obvious.
                  </>,
                  <>
                    ¿La diferencia de precio? 11 dólares al mes (ilegal) frente
                    a 25-60 dólares al mes (legal y seguro). La elección es
                    obvia.
                  </>,
                  <>
                    La différence de prix&nbsp;? 11&nbsp;$ par mois (illégal)
                    contre 25 à 60&nbsp;$ par mois (légal et sécurisé). Le choix
                    est évident.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비용
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      차이는?
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      월
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      11달러(불법)
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      월
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      25~60달러(합법적이고
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      안전함).
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      선택은
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      명확합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id={'mistake-2-playing-personal-favorites'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>
                    Mistake #2: Playing Personal Favorites Instead of
                    Customer-Focused Music
                  </>,
                  <>
                    Error 2: Poner música que te gusta a ti en lugar de música
                    centrada en los gustos del cliente
                  </>,
                  <>
                    Erreur 2&nbsp;: Diffuser ses morceaux préférés au lieu de
                    diffuser de la musique adaptée aux goûts des clients.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      두
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      번째
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      실수:
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      고객
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      중심적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      대신
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      개인적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      취향에
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      맞는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음악을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      트는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id={'mistake-2-problem-defined'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>The Problem</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>El problema</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>Le problème</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문제점
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Just because you love 1970s classic rock doesn’t mean your
                      Gen Z boutique customers do. This wrong music for the
                      audience is a frequent and very costly error.
                    </>,
                    <>
                      Que a ti te guste el rock clásico de los años 70 no
                      significa que a tus clientas de la Generación Z también
                      les guste. Elegir la música equivocada para el público es
                      un error frecuente y muy costoso.
                    </>,
                    <>
                      Ce n'est pas parce que vous aimez le rock classique des
                      années 1970 que vos clients de la génération Z, qui
                      fréquentent votre boutique, partagent vos goûts. Choisir
                      une musique inadaptée à son public est une erreur
                      fréquente et très coûteuse.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        당신이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        1970년대
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        클래식
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        록을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        좋아한다고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        해서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        당신의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        Z세대
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        부티크
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고객들도
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        좋아할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        거라고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        생각하면
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        안
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        됩니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        청중에게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        맞지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        않는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        트는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        흔하면서도
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        매우
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        큰
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        손실을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        초래하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        실수입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id={'mistake-2-significance'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Why It Matters</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Por qué es importante
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Pourquoi c'est important
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        왜
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        중요한가
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        The music is a cue to say, “This place is for people
                        like me.”
                      </>,
                      <>
                        La música es una señal que indica: "Este lugar es para
                        personas como yo"
                      </>,
                      <>
                        La musique sert d'indice pour dire&nbsp;: «&nbsp;Cet
                        endroit est fait pour des gens comme moi&nbsp;»
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          "이곳은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          같은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사람들을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          위한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          곳이야"라고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          말해주는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          신호입니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Demographic misalignment can reduce dwell time by 25%
                      </>,
                      <>
                        La falta de coincidencia demográfica puede reducir el
                        tiempo de permanencia en un 25%.
                      </>,
                      <>
                        Un décalage démographique peut réduire le temps de
                        présence de 25&nbsp;%
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          인구
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          통계학적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          불일치는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          체류
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시간을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          25%
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          감소시킬
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          있습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Wrong genre decreases purchase likelihood significantly
                      </>,
                      <>
                        Elegir el género incorrecto disminuye significativamente
                        la probabilidad de compra.
                      </>,
                      <>
                        Choisir le mauvais genre réduit considérablement la
                        probabilité d'achat
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          잘못된
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          장르
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          선택은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구매
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가능성을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          크게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          떨어뜨립니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Customers walk away without making purchases when the
                        atmosphere feels “off.”
                      </>,
                      <>
                        Los clientes se marchan sin realizar ninguna compra
                        cuando el ambiente no les resulta agradable.
                      </>,
                      <>
                        Les clients repartent sans faire d'achats lorsque
                        l'ambiance leur semble «&nbsp;étrange&nbsp;»
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          매장
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          분위기가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          어색하거나
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          불편하게
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          느껴지면
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          고객들은
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구매하지
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          않고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          발길을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          돌립니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Real example: A 65-year-old café owner played music from
                      her youth. Her target customers (remote workers aged
                      25–35) stopped coming. After changing the music playlists
                      to indie-acoustic, matching customer demographics, dwell
                      time was 40 times higher, and daily revenue was 28 times
                      higher.
                    </>,
                    <>
                      Ejemplo real: La dueña de una cafetería de 65 años ponía
                      música de su juventud. Sus clientes habituales
                      (trabajadores a distancia de entre 25 y 35 años) dejaron
                      de ir. Tras cambiar las listas de reproducción a música
                      indie-acústica, más acorde con la edad de sus clientes, el
                      tiempo de permanencia aumentó 40 veces y los ingresos
                      diarios se multiplicaron por 28.
                    </>,
                    <>
                      Exemple concret&nbsp;: Une propriétaire de café de 65 ans
                      diffusait de la musique de sa jeunesse. Ses clients cibles
                      (télétravailleurs âgés de 25 à 35 ans) ont cessé de
                      fréquenter son établissement. Après avoir modifié les
                      listes de lecture pour proposer de la musique
                      indie-acoustique, plus adaptée à la clientèle, le temps de
                      présence des clients a été multiplié par 40 et le chiffre
                      d'affaires quotidien par 28.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        실제
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사례:
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        65세의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        카페
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        주인은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        자신이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        젊었을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        때
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        듣던
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        틀었습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        그러자
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        주요
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고객층(25~35세의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        재택근무자)들이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        더
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이상
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        카페에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        않게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        되었습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        플레이리스트를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고객층에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        맞는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        인디
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        어쿠스틱
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        바꾸자,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고객들의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        체류
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        시간이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        40배
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        증가했고,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        일일
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        매출은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        28배
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        증가했습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id={'mistake-2-how-to-fix'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>The Fix</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>La solución</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>La solution</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수정
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={MAIN_SITE_ENVIRONMENT_HOST}
                        title={
                          'Optimize restaurant music playlists with Practical Stream, the commercial background music streaming service'
                        }
                        rel="noopener noreferrer"
                      >
                        <span translate="no">
                          Strategic commercial music{' '}
                          <span className={classes.spanPre}>
                            selection&nbsp;
                            <IconExternalLink
                              size={width < 500 ? 17 : width < 920 ? 20 : 24}
                              width={width < 500 ? 17 : width < 920 ? 20 : 24}
                              height={width < 500 ? 17 : width < 920 ? 20 : 24}
                            />
                          </span>
                        </span>
                      </a>{' '}
                      begins with understanding your customer:
                    </>,
                    <>
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={MAIN_SITE_ENVIRONMENT_HOST_ES}
                        title={
                          'Optimiza las listas de reproducción de música de tu restaurante con Practical Stream, el servicio de transmisión de música ambiental para negocios.'
                        }
                        rel="noopener noreferrer"
                      >
                        <span translate="no">
                          La selección estratégica de{' '}
                          <span className={classes.spanPre}>
                            música&nbsp;
                            <IconExternalLink
                              size={width < 500 ? 17 : width < 920 ? 20 : 24}
                              width={width < 500 ? 17 : width < 920 ? 20 : 24}
                              height={width < 500 ? 17 : width < 920 ? 20 : 24}
                            />
                          </span>
                        </span>
                      </a>{' '}
                      comercial comienza por comprender a su cliente:
                    </>,
                    <>
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={MAIN_SITE_ENVIRONMENT_HOST_FR}
                        title={
                          "Optimisez les playlists musicales de votre restaurant avec Practical Stream, le service de diffusion de musique d'ambiance pour les commerces."
                        }
                        rel="noopener noreferrer"
                      >
                        <span translate="no">
                          La{' '}
                          <span className={classes.spanPre}>
                            sélection&nbsp;
                            <IconExternalLink
                              size={width < 500 ? 17 : width < 920 ? 20 : 24}
                              width={width < 500 ? 17 : width < 920 ? 20 : 24}
                              height={width < 500 ? 17 : width < 920 ? 20 : 24}
                            />
                          </span>
                        </span>
                      </a>{' '}
                      stratégique de musique à des fins commerciales commence
                      par la compréhension de votre clientèle&nbsp;:
                    </>,
                    <>
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={MAIN_SITE_ENVIRONMENT_HOST}
                        title={
                          'Optimize restaurant music playlists with Practical Stream, the commercial background music streaming service'
                        }
                        rel="noopener noreferrer"
                      >
                        <span translate="no">
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            전략적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            상업용
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>{' '}
                          <span className={classes.spanPre}>
                            선정은&nbsp;
                            <IconExternalLink
                              size={width < 500 ? 17 : width < 920 ? 20 : 24}
                              width={width < 500 ? 17 : width < 920 ? 20 : 24}
                              height={width < 500 ? 17 : width < 920 ? 20 : 24}
                            />
                          </span>
                        </span>
                      </a>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고객을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이해하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        시작됩니다:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <section id={'mistake-2-step-1-of-fix-define-demographic'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Step 1: Define Your Target Demographic
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Paso 1: Define tu público objetivo
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Étape 1&nbsp;: Définissez votre public cible
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          1단계:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          목표
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          고객층을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          정의하세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>Age range (primary and secondary)</>,
                        <>Rango de edad (primaria y secundaria)</>,
                        <>Tranche d'âge (primaire et secondaire)</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            연령대
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (초등학생
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            및
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            중학생)
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Lifestyle and values</>,
                        <>Estilo de vida y valores</>,
                        <>Mode de vie et valeurs</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            생활
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            방식과
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            가치관
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Cultural preferences</>,
                        <>Preferencias culturales</>,
                        <>Préférences culturelles</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            문화적
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            선호도
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Income level and shopping behaviors</>,
                        <>Nivel de ingresos y hábitos de compra</>,
                        <>Niveau de revenu et comportements d'achat</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            소득
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수준과
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            쇼핑
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            행동
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
                <section id={'mistake-2-step-2-of-fix-match-to-brand'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Step 2: Match Music to Brand Promise
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Paso 2: Relacionar la música con la promesa de la
                          marca.
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Étape 2&nbsp;: Associer la musique à la promesse de la
                          marque
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          2단계:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음악을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          브랜드
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          약속에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          맞춰
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          선택하세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <div className={classes.dataTableWrapper}>
                    <table className={classes.dataTable}>
                      <caption className={classes.dtCaption}>
                        {translate(
                          <>
                            Music Strategy and Target Customer by Business Type
                          </>,
                          <>
                            Musical strategies and target customers by type of
                            business
                          </>,
                          <>
                            Stratégies musicales et clients cibles par type
                            d'entreprise
                          </>,
                          <>
                            <span
                              aria-hidden={true}
                              className={classes.asianTextBlock}
                            >
                              사업 유형별 음악 전략 및 타겟 고객
                            </span>
                          </>,
                          selectedLanguage
                        )}
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col" className={classes.dataTableHeader}>
                            {translate(
                              <>Business&nbsp;Type</>,
                              <>Tipo&nbsp;de&nbsp;negocio</>,
                              <>Type&nbsp;d'entreprise</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  사업
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  유형
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </th>
                          <th scope="col" className={classes.dataTableHeader}>
                            {translate(
                              <>Target&nbsp;Customer</>,
                              <>Cliente&nbsp;objetivo</>,
                              <>Client&nbsp;cible</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  타겟
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  고객
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </th>
                          <th scope="col" className={classes.dataTableHeader}>
                            {translate(
                              <>Music&nbsp;Strategy</>,
                              <>Estrategia&nbsp;musical</>,
                              <>Stratégie&nbsp;musicale</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  음악
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  전략
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>Luxury Boutique</>,
                              <>Boutique de lujo</>,
                              <>Boutique de luxe</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  럭셔리
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  부티크
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>35-55, high income</>,
                              <>35-55 años, ingresos altos</>,
                              <>35-55 ans, revenus élevés</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  35세~55세,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  고소득층
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>Classical, sophisticated jazz</>,
                              <>Jazz clásico y sofisticado</>,
                              <>Jazz classique et sophistiqué</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  클래식하고
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  세련된
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  재즈
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>Youth fashion</>,
                              <>Moda juvenil</>,
                              <>Mode pour les jeunes</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  청소년
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  패션
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>16-28, trend-focused</>,
                              <>16-28, centrada en las tendencias</>,
                              <>16-28 ans, axé sur les tendances</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  16세~28세,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  트렌드에
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  민감한
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>Current indie, pop, electronic</>,
                              <>Indie actual, pop, electrónica</>,
                              <>
                                Musique indépendante, pop et électronique
                                actuelles
                              </>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  최신
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  인디,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  팝,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  일렉트로닉
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  음악
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>Family restaurant</>,
                              <>Restaurante para familias</>,
                              <>Restaurant familial</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  가족
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  레스토랑
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>All ages</>,
                              <>Todas las edades</>,
                              <>Tous les âges</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  모든
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  연령대
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>Clean, broadly appealing hits</>,
                              <>
                                Éxitos limpios y de gran atractivo para el
                                público
                              </>,
                              <>Des succès propres et largement appréciés</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  깔끔하고
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  폭넓게
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  사랑받는
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  히트곡들
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>Wellness spa</>,
                              <>Balneario de bienestar</>,
                              <>Spa bien-être</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  웰니스
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  스파
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>30-50, health-conscious</>,
                              <>30-50 años, preocupados por la salud</>,
                              <>30-50 ans, soucieux de leur santé</>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  30~50세,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  건강에
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  관심이
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  많은
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  사람
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                          <td className={classes.dataTableField}>
                            {translate(
                              <>Ambient, acoustic, world music</>,
                              <>Música ambiental, acústica, música del mundo</>,
                              <>
                                Musique d'ambiance, acoustique, musiques du
                                monde
                              </>,
                              <>
                                <span
                                  aria-hidden={true}
                                  className={classes.asianTextBlock}
                                >
                                  앰비언트,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  어쿠스틱,
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  월드
                                </span>{' '}
                                <span
                                  className={classes.asianTextBlock}
                                  aria-hidden={true}
                                >
                                  뮤직
                                </span>
                              </>,
                              selectedLanguage
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                          <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                          <td style={{ lineHeight: 0.005 }}>&nbsp;</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>
                <section id={'mistake-2-step-3-of-fix-test-and-refine'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Step 3: Test and Refine
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Paso 3: Probar y perfeccionar
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Étape 3&nbsp;: Tester et affiner
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          3단계:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          테스트
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          및
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          개선
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>Observe customer behavioral change</>,
                        <>
                          Observar los cambios en el comportamiento del cliente
                        </>,
                        <>
                          Observer les changements de comportement des clients
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            고객
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            행동
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            변화를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관찰하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Collect feedback in the form of surveys</>,
                        <>Recopile comentarios a través de encuestas</>,
                        <>Recueillez des commentaires sous forme de sondages</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            설문조사
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            형태로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            피드백을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수집하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Track sales correlation</>,
                        <>Seguimiento de la correlación de ventas</>,
                        <>Suivre la corrélation des ventes</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            판매
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            상관
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관계
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            추적
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Adjust based on data</>,
                        <>Ajustar en función de los datos</>,
                        <>Ajuster en fonction des données</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            데이터를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            기반으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            조정하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Background music isn't about your preference&nbsp;— it's
                      about your customer's comfort and connection.
                    </>,
                    <>
                      La música de fondo no se trata de tus preferencias, sino
                      de la comodidad y la conexión con tus clientes.
                    </>,
                    <>
                      La musique d'ambiance ne dépend pas de vos préférences,
                      mais du confort et du bien-être de vos clients.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        배경
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        당신의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        취향에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        관한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        아니라,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고객의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        편안함과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        공감대를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        형성하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        데
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        관한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
            </section>
            <section id={'mistake-3-ignoring-volume-matters'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>
                    Mistake #3: Ignoring Volume Management Throughout the Day
                  </>,
                  <>
                    Error 3: Ignorar la gestión del volumen a lo largo del día
                  </>,
                  <>
                    Erreur 3&nbsp;: Négliger la gestion du volume sonore tout au
                    long de la journée
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      세
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      번째
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      실수:
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하루
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      종일
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음량
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      관리를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소홀히
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id={'mistake-3-problem-defined'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>The Problem</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>El problema</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>Le problème</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문제점
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Fixed volume controls produce ineffective acoustics in
                      stores that irritate both shoppers and employees. Volume
                      that is effective at 10 AM doesn’t work at 8 PM.
                    </>,
                    <>
                      Los controles de volumen fijos producen una acústica
                      ineficaz en las tiendas, lo que resulta molesto tanto para
                      los clientes como para los empleados. Un volumen adecuado
                      a las 10 de la mañana no lo es a las 8 de la tarde.
                    </>,
                    <>
                      Les réglages de volume fixes produisent une acoustique
                      inefficace dans les magasins, ce qui irrite aussi bien les
                      clients que les employés. Un volume adapté à 10&nbsp;h du
                      matin ne convient pas à 20&nbsp;h.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고정된
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        볼륨
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        설정은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        매장에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        비효율적인
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음향
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        효과를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        초래하여
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        쇼핑객과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        직원
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        모두에게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        불편함을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        줍니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오전
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        10시에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        적절한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        볼륨이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오후
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        8시에는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        적합하지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        않을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        수
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      The science: As per studies, customers spend an extra 3.3
                      minutes and $2.20 more with every decibel increment.
                      However, there is a breaking point&nbsp;— excessive
                      loudness drives customers away.
                    </>,
                    <>
                      La ciencia lo confirma: según los estudios, los clientes
                      pasan 3,3 minutos más y gastan 2,20 dólares adicionales
                      por cada decibelio de aumento del volumen. Sin embargo,
                      existe un límite: un volumen excesivo ahuyenta a los
                      clientes.
                    </>,
                    <>
                      L'explication scientifique&nbsp;: Selon des études, les
                      clients passent 3,3 minutes de plus et dépensent 2,20
                      dollars supplémentaires pour chaque décibel
                      supplémentaire. Cependant, il y a une limite&nbsp;: un
                      volume sonore excessif fait fuir les clients.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        과학적
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        분석에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        따르면,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        연구
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        결과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고객들은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소음이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        1데시벨
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        증가할
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        때마다
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        평균
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        3.3분을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        더
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        머물고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        2.20달러를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        더
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소비하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        나타났습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        하지만
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        여기에는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        한계점이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        지나치게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        시끄러운
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소음은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        오히려
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고객들을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        쫓아내게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        됩니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>Volume impact guidelines:</>,
                    <>Directrices sobre el impacto del volumen:</>,
                    <>Directives relatives à l'impact du volume&nbsp;:</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        볼륨
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        영향
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        지침:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>60–70 dB: Comfortable conversation, extended stays</>,
                      <>60–70 dB: Conversación cómoda, estancias prolongadas</>,
                      <>
                        60 à 70 dB&nbsp;: Conversation confortable, séjours
                        prolongés
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          60~70dB:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          편안한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대화가
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          가능하며
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          장시간
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          머물기에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          적합한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수준입니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>1–75 dB: Safe, slightly louder conversations</>,
                      <>
                        1–75 dB: Nivel seguro, conversaciones ligeramente más
                        ruidosas
                      </>,
                      <>
                        1 à 75 dB&nbsp;: Niveau sonore sans danger,
                        conversations légèrement plus fortes
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          1~75dB:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          안전한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          수준이며,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          약간
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          큰
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          목소리로
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대화하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          정도의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          소음입니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>76–80 dB: Loud, difficult conversation</>,
                      <>76–80 dB: Ruido fuerte, dificulta la conversación</>,
                      <>76 à 80 dB&nbsp;: Bruyant, conversation difficile</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          76~80dB:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시끄러워서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대화하기
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          어려움
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        81+ dB: Very loud, endangers hearing, unconducive to
                        conversation
                      </>,
                      <>
                        81+ dB: Muy ruidoso, peligroso para el oído, dificulta
                        la conversación
                      </>,
                      <>
                        81 dB et plus&nbsp;: Très bruyant, dangereux pour
                        l'ouïe, rend la conversation difficile
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          81데시벨
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이상:
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          매우
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시끄러움,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          청력에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          해로움,
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          대화하기에
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          부적합함
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      According to Zagat surveys, noise has become the second
                      most common complaint in U.S. restaurants after poor
                      service.
                      <br />
                      See this{' '}
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={
                          'https://pmc.ncbi.nlm.nih.gov/articles/PMC11673941/'
                        }
                        title={
                          'Noise Pollution in Restaurants - Case Study - All Noise Control'
                        }
                        rel="noopener noreferrer"
                      >
                        deep{' '}
                        <span className={classes.spanPre}>
                          insight&nbsp;
                          <IconExternalLink />
                        </span>
                      </a>
                    </>,
                    <>
                      Según las encuestas de Zagat, el ruido se ha convertido en
                      la segunda queja más común en los restaurantes de Estados
                      Unidos, después del mal servicio.
                      <br />
                      Consulta esto para obtener{' '}
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={
                          'https://pmc.ncbi.nlm.nih.gov/articles/PMC11673941/'
                        }
                        title={
                          'Contaminación acústica en restaurantes - Estudio de caso - Todo el Control de Ruido'
                        }
                        rel="noopener noreferrer"
                      >
                        una visión más{' '}
                        <span className={classes.spanPre}>
                          profunda&nbsp;
                          <IconExternalLink />
                        </span>
                      </a>
                    </>,
                    <>
                      Selon les enquêtes de Zagat, le bruit est devenu la
                      deuxième plainte la plus fréquente dans les restaurants
                      américains, après la mauvaise qualité du service.
                      <br />
                      Voir cette{' '}
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={
                          'https://pmc.ncbi.nlm.nih.gov/articles/PMC11673941/'
                        }
                        title={
                          'Pollution sonore dans les restaurants - Étude de cas - Tous les contrôles du bruit'
                        }
                        rel="noopener noreferrer"
                      >
                        analyse{' '}
                        <span className={classes.spanPre}>
                          approfondie&nbsp;
                          <IconExternalLink />
                        </span>
                      </a>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자갓(Zagat)
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        설문조사에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        따르면,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        소음은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        미국
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        레스토랑에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        형편없는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        서비스에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        이어
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        두
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        번째로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        흔한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        불만
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사항이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        되었습니다.
                      </span>
                      According to Zagat surveys, noise has become the second
                      most common complaint in U.S. restaurants after poor
                      service.
                      <br />
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href={
                          'https://pmc.ncbi.nlm.nih.gov/articles/PMC11673941/'
                        }
                        title={
                          '레스토랑 소음 공해 문제 - 사례 연구 - 올 노이즈 컨트롤'
                        }
                        rel="noopener noreferrer"
                      >
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          심오한
                        </span>{' '}
                        <span className={classes.spanPre}>
                          통찰력을&nbsp;
                          <IconExternalLink />
                        </span>
                      </a>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보세요
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id={'mistake-3-how-to-fix'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>The Fix</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>La solución</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>La solution</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수정
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>Dynamic volume management strategy:</>,
                    <>Estrategia de gestión dinámica del volumen:</>,
                    <>Stratégie de gestion dynamique du volume&nbsp;:</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동적
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        볼륨
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        관리
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전략:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <section id={'mistake-3-fix-by-time-of-day'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          By time of day:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Por hora del día:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Par heure de la journée&nbsp;:
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          시간대별로:
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>Morning (low traffic): 60–65 dB</>,
                        <>Por la mañana (poco tráfico): 60–65 dB</>,
                        <>Matin (faible circulation)&nbsp;: 60–65 dB</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            아침
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (교통량이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            적을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            때):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            60~65dB
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Midday (moderate): 65–70 dB</>,
                        <>Mediodía (moderada): 65–70 dB</>,
                        <>En milieu de journée (modéré)&nbsp;: 65–70 dB</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            정오
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (보통):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            65~70dB
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Peak hours: 70–75 dB (adjust for crowd)</>,
                        <>
                          Horas punta: 70-75 dB (ajustar según la afluencia de
                          público)
                        </>,
                        <>
                          Heures de pointe&nbsp;: 70 à 75 dB (à ajuster en
                          fonction de l'affluence)
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            피크
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간대:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            70~75dB
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (인파
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            규모에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            따라
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            조정
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            필요)
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Late evening: 65–70 dB</>,
                        <>Últimas horas de la tarde: 65–70 dB</>,
                        <>Fin de soirée&nbsp;: 65–70 dB</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            늦은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            저녁:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            65~70dB
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
                <section id={'mistake-3-fix-by-business-goal'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          By business goal:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Según el objetivo empresarial:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Par objectif commercial&nbsp;:
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          사업
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          목표별:
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Extended stays (cafes, bookstores): Lower volume
                          (60–65 dB)
                        </>,
                        <>
                          Estancias prolongadas (cafeterías, librerías): Volumen
                          bajo (60–65&nbsp;dB)
                        </>,
                        <>
                          Séjours prolongés (cafés, librairies)&nbsp;: Niveau
                          sonore plus faible (60-65 dB)
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            장시간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            체류
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간(카페,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            서점):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            낮은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소음
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수준(60~65dB)
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Quick turnover (fast-casual): Moderate-high (70–75 dB)
                        </>,
                        <>
                          Rotación rápida de clientes (restaurantes de comida
                          rápida informal): Nivel de ruido moderado-alto (70–75
                          dB)
                        </>,
                        <>
                          Rotation rapide des clients (restauration rapide
                          décontractée)&nbsp;: Niveau sonore modéré à élevé
                          (70-75 dB)
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            빠른
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            회전율
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (패스트
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            캐주얼):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            중간-높음
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (70~75dB)
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Social atmosphere (bars): Higher volume (75–80 dB)</>,
                        <>
                          Ambiente social (bares): Volumen más alto (75–80 dB)
                        </>,
                        <>
                          Ambiance sociale (tavernes)&nbsp;: Volume sonore plus
                          élevé (75–80 dB)
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            사회적
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            분위기
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (술집):
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소음
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수준
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            높음
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (75~80dB)
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
                <section id={'mistake-3-fix-implementation'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Implementation:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Implementación:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Mise en œuvre&nbsp;:
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>Train staff on volume adjustment</>,
                        <>Capacitar al personal sobre el ajuste de volumen</>,
                        <>Former le personnel à l'ajustement du volume</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            구현:
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Use automated scheduling if available</>,
                        <>
                          Utilice la programación automatizada si está
                          disponible
                        </>,
                        <>
                          Utilisez la planification automatisée si elle est
                          disponible
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            가능하다면
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            자동
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            예약
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            기능을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사용하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Analyze customer feedback</>,
                        <>Analizar los comentarios de los clientes</>,
                        <>Analyser les commentaires des clients</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            고객
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            피드백을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            분석하세요.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Adjust for crowd size dynamically</>,
                        <>Ajuste el tamaño de la multitud de forma dinámica</>,
                        <>Ajuster la taille de la foule de manière dynamique</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            군중
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            규모에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            따라
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            동적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            조정합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
              </section>
            </section>
            <section id={'mistake-4-playlists-without-design'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>
                    Mistake #4: No Strategy&nbsp;— Random Playlists Without
                    Purpose
                  </>,
                  <>
                    Error 4: Falta de estrategia: listas de reproducción
                    aleatorias sin ningún propósito.
                  </>,
                  <>
                    Erreur 4&nbsp;: Absence de stratégie&nbsp;– Listes de
                    lecture aléatoires sans objectif
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      #4:
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      전략
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      부재
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      —
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      목적
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      없이
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      무작위로
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      구성된
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      플레이리스트
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id={'mistake-4-problem-defined'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>The Problem</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>El problema</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>Le problème</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문제점
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Shuffle mode isn’t a strategy. Chaotic business playlist
                      mistakes, such as jumping from classical to heavy metal to
                      country in 15 minutes, ruin the ambience and disorient the
                      customer.
                    </>,
                    <>
                      El modo aleatorio no es una estrategia. Una lista de
                      reproducción caótica en un negocio, con errores como pasar
                      de música clásica a heavy metal y luego a música country
                      en 15 minutos, arruina el ambiente y desorienta al
                      cliente.
                    </>,
                    <>
                      Le mode aléatoire n'est pas une stratégie. Une liste de
                      lecture chaotique pour votre commerce, avec des erreurs
                      comme passer de la musique classique au heavy metal puis à
                      la musique country en 15 minutes, gâche l'ambiance et
                      désoriente le client.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        랜덤
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        재생
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        모드는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        전략이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        아닙니다.
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        클래식
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악에서
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        헤비메탈,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        컨트리
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악으로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        15분
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        만에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        넘나드는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것과
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        같은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        무질서한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        비즈니스
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        플레이리스트는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        분위기를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        망치고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고객에게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        혼란을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        줄
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        수
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <section id={'mistake-4-why-consistency-matters'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Why consistency matters:
                        </span>
                      </>,
                      <>Por qué la coherencia es importante:</>,
                      <>
                        <span className={classes.spanBolder}>
                          Pourquoi la cohérence est importante&nbsp;:
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          일관성이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          중요한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          이유:
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Predictable atmosphere creates psychological comfort
                        </>,
                        <>Un ambiente predecible crea comodidad psicológica</>,
                        <>
                          Une atmosphère prévisible crée un confort
                          psychologique
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            예측
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            가능한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            분위기는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            심리적
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            편안함을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            조성합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Genre jumping is subconsciously uncomfortable</>,
                        <>
                          Cambiar de género musical resulta incómodo a nivel
                          subconsciente
                        </>,
                        <>
                          Changer de genre musical est inconsciemment
                          inconfortable
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            장르를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            넘나드는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            것은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            무의식적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          ></span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Service speed should be matched by the tempo of music
                        </>,
                        <>
                          La velocidad del servicio debe ir acorde con el ritmo
                          de la música
                        </>,
                        <>
                          La rapidité du service doit être en adéquation avec le
                          tempo de la musique
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            서비스
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            속도는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            템포와
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            맞춰져야
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          A brand's experience should be reflected in its
                          emotional tone
                        </>,
                        <>
                          La experiencia de una marca debe reflejarse en su tono
                          emocional.
                        </>,
                        <>
                          L'expérience d'une marque doit se refléter dans sa
                          tonalité émotionnelle
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            브랜드의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경험은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            브랜드의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            감성적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            톤에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            반영되어야
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
                <section id={'mistake-4-examples-of-bad-shopping-music'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Examples of poor sound choices in retail:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Ejemplos de malas decisiones en cuanto al sonido en
                          establecimientos comerciales:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Exemples de mauvais choix sonores dans le commerce de
                          détail&nbsp;:
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          소매점에서
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          부적절한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          음향
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          선택의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          예:
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>Sad breakup songs during happy hour</>,
                        <>Canciones tristes de desamor durante la hora feliz</>,
                        <>
                          Des chansons de rupture tristes pendant l'happy hour
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            해피아워
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            듣는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            슬픈
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이별
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            노래들
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Genre jumping is subconsciously uncomfortable</>,
                        <>
                          Cambiar de género musical resulta incómodo a nivel
                          subconsciente
                        </>,
                        <>
                          Le passage d'un genre à l'autre est inconsciemment
                          inconfortable
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            장르를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            넘나드는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            것은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            무의식적으로
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            불편함을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            유발한다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Aggressive music during spa services</>,
                        <>Música agresiva durante los servicios de spa.</>,
                        <>Musique agressive pendant les soins au spa</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            스파
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            서비스
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            중에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공격적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음악이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            흘러나온다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Fast tempo during fine dining</>,
                        <>Ritmo acelerado durante una cena elegante</>,
                        <>Rythme rapide pendant un repas gastronomique</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            고급
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            식사
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            중
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            빠른
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            템포
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
              </section>
              <section id={'mistake-4-how-to-fix'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>The Fix</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>La solución</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>La solution</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수정
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>Curate purpose-driven playlists.</>,
                    <>
                      Crea listas de reproducción con un propósito específico.
                    </>,
                    <>Créez des listes de lecture thématiques et ciblées.</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목적에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        맞는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        플레이리스트를
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        만들어
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        보세요.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <section id={'mistake-4-how-to-fix-in-morning-shifts'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Morning/Opening (7 AM — 11 AM)
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Mañana/Apertura (7:00 a. m. - 11:00 a. m.)
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Matin/Ouverture (7h00 — 11h00)
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          오전/개장
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시간
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (오전
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          7시
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          ~
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오전
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          11시)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>Tempo: 80–95 BPM</>,
                        <>Ritmo: 80–95 BPM</>,
                        <>Tempo&nbsp;: 80–95 BPM</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            템포:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            80~95
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            BPM
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Mood: Uplifting, energizing</>,
                        <>Estado de ánimo: Estimulante, energizante</>,
                        <>Ambiance&nbsp;: Inspirante, énergisante</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            분위기:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            기분
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전환,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            활력
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            넘치는
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Goal: Welcome and energize customers</>,
                        <>
                          Objetivo: Dar la bienvenida y motivar a los clientes
                        </>,
                        <>
                          Objectif&nbsp;: Accueillir et dynamiser les clients
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            목표:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            고객을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            환영하고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            활력을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            불어넣습니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
                <section id={'mistake-4-how-to-fix-in-midday-shifts'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Midday/Peak (11 AM — 3 PM)
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Mediodía/Hora punta (11:00 a. m. - 3:00 p. m.)
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Midi/Heures de pointe (11h00 - 15h00)
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          정오/피크
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시간대
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (오전
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          11시
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          ~
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오후
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          3시)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>Tempo: 85–100 BPM</>,
                        <>Ritmo: 85–100 BPM</>,
                        <>Tempo&nbsp;: 85 à 100 BPM</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            템포:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            85~100
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            BPM
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Mood: Engaging, professional</>,
                        <>Estado de ánimo: Atento, profesional</>,
                        <>Ambiance&nbsp;: Engageante, professionnelle</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            분위기:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            매력적이고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전문적인
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Goal: Maintain energy, manage flow</>,
                        <>Objetivo: Mantener la energía, gestionar el flujo.</>,
                        <>Objectif&nbsp;: Maintenir l'énergie, gérer le flux</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            목표:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            에너지
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            유지,
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            흐름
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            관리
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
                <section id={'mistake-4-how-to-fix-in-afternoon-shifts'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Afternoon (3 PM — 6 PM)
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Tarde (15:00 - 18:00)
                        </span>
                      </>,
                      <></>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          오후
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (오후
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          3시
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          ~
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          오후
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          6시)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>Tempo: 75–90 BPM</>,
                        <>Ritmo: 75–90 BPM</>,
                        <>Tempo&nbsp;: 75 à 90 BPM</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            템포:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            75~90
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            BPM
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Mood: Comfortable, consistent</>,
                        <>Estado de ánimo: Cómodo, estable</>,
                        <>Ambiance&nbsp;: Confortable, constante</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            분위기:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            편안하고
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            안정적임
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Goal: Sustained comfort</>,
                        <>Objetivo: Comodidad duradera</>,
                        <>Objectif&nbsp;: Confort durable</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            목표:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            지속적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            편안함
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
                <section id={'mistake-4-how-to-fix-in-evening-shifts'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Evening (6&nbsp;PM&nbsp;— Close)
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Noche (18:00&nbsp;— Cierre)
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Soirée (18h00&nbsp;— Fermeture)
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          저녁
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          (오후
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          6시
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          ~
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          마감)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>Tempo: Varies by concept</>,
                        <>Ritmo: Varía según el concepto</>,
                        <>Rythme&nbsp;: Varie selon le concept</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            소요
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시간:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            콘셉트에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            따라
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            다릅니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Mood: Matches closing experience</>,
                        <>Estado de ánimo: Experiencia de partidos de cierre</>,
                        <>
                          Ambiance&nbsp;: Correspond à l'expérience vécue lors
                          de la clôture des matchs
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            분위기:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경기가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            끝날
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            때의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            경험과
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            일치합니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Goal: Wind down or amp up social</>,
                        <>Objetivo: Relajarse o animar el ambiente social</>,
                        <>
                          Objectif&nbsp;: Se détendre ou dynamiser sa vie
                          sociale
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            목표:
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사교
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            활동을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            줄이거나
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            늘리기
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
              </section>
            </section>
            <section id={'mistake-5-bad-sound-quality'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>
                    Mistake #5: Compromising on Sound Quality and Reliability
                  </>,
                  <>
                    Error 5: Comprometer la calidad del sonido y la fiabilidad
                  </>,
                  <>
                    Erreur 5&nbsp;: Faire des compromis sur la qualité sonore et
                    la fiabilité
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실수
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      #5:
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      음질과
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      신뢰성을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      타협하는
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      것
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id={'mistake-5-problem-defined'}>
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>The Problem</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>El problema</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>Le problème</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문제점
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Cheap speakers, buffering music, and dead air during
                      customer visits are indicators of poor business, no matter
                      how high-quality your products are.
                    </>,
                    <>
                      Altavoces de baja calidad, música que se interrumpe
                      constantemente y silencios incómodos durante las visitas
                      de los clientes son indicadores de una mala gestión
                      empresarial, por muy buenos que sean tus productos.
                    </>,
                    <>
                      Des enceintes bon marché, des coupures de musique et des
                      silences gênants lors des visites de clients sont des
                      signes de mauvaise gestion, quelle que soit la qualité de
                      vos produits.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        저렴한
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        스피커,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        끊기는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악,
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        그리고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        고객
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        방문
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        중에
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        발생하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        침묵은
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        제품의
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        품질이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        아무리
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        뛰어나더라도
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        사업
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        운영이
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        제대로
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        되고
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        있지
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        않다는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        것을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        보여주는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        지표입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <section id={'mistake-5-common-technical-issues'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Common technical issues:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Problemas técnicos comunes:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Problèmes techniques des communes&nbsp;:
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          일반적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          기술적
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          문제:
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>Single small speaker for a large space</>,
                        <>Un solo altavoz pequeño para un espacio grande</>,
                        <>Un seul petit haut-parleur pour un grand espace</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            넓은
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            적합한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소형
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            스피커
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            하나
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Bluetooth speakers that disconnect</>,
                        <>Altavoces Bluetooth que se desconectan</>,
                        <>Haut-parleurs Bluetooth qui se déconnectent</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            연결이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            끊기는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            블루투스
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            스피커
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Consumer services with ads and interruptions</>,
                        <>
                          Servicios para el consumidor con anuncios e
                          interrupciones
                        </>,
                        <>
                          Services aux consommateurs avec publicités et
                          interruptions
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            광고와
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            방해가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            포함된
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            소비자
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            서비스
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>No backup when the internet fails</>,
                        <>No hay respaldo cuando falla internet.</>,
                        <>
                          Aucune solution de secours en cas de panne d'internet
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            인터넷이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            끊기면
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            백업
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수단이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            없습니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
                <section id={'mistake-5-equipment-requirements'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Equipment Requirements:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Requisitos de equipo:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Exigences en matière d'équipement&nbsp;:
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장비
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          요구
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          사항:
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Commercial-grade speakers are properly placed
                          throughout the space.
                        </>,
                        <>
                          Se han colocado altavoces de calidad profesional en
                          los lugares adecuados de todo el espacio
                        </>,
                        <>
                          Des haut-parleurs de qualité professionnelle sont
                          correctement positionnés dans tout l'espace
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            상업용
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            스피커가
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            공간
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            전체에
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            적절하게
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            배치되어
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있습니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Multiple speakers for even coverage (no dead zones)
                        </>,
                        <>
                          Múltiples altavoces para una cobertura uniforme (sin
                          zonas muertas)
                        </>,
                        <>
                          Plusieurs haut-parleurs pour une couverture uniforme
                          (sans zones mortes)
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            균일한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            음향
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            커버리지를
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            위한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            여러
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            개의
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            스피커
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            (사각지대
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            없음)
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Professional installation and calibration</>,
                        <>Instalación y calibración profesional</>,
                        <>Installation et étalonnage professionnels</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            전문적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            설치
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            및
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            보정
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            서비스
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Backup systems when technology fails</>,
                        <>
                          Sistemas de respaldo para cuando la tecnología falla
                        </>,
                        <>
                          Systèmes de secours en cas de défaillance
                          technologique
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            기술
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            장애
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            발생
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            사용할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            백업
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            시스템
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
                <section id={'mistake-5-service-standards'}>
                  <h4 className={classes.pParagraph}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Service Standards:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Estándares de servicio:
                        </span>
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Normes de service&nbsp;:
                        </span>
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          서비스
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          표준:
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </h4>
                  <ul
                    className={classes.standardText + ' ' + classes.ulArticles}
                  >
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Licensed commercial streaming with zero interruptions
                        </>,
                        <>
                          Transmisión comercial con licencia y sin
                          interrupciones
                        </>,
                        <>
                          Diffusion en continu commerciale sous licence, sans
                          aucune interruption
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            중단
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            없이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            제공되는
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            정식
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            라이선스
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            상업용
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            스트리밍
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            서비스
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Reliable internet connection with backup options</>,
                        <>
                          Conexión a internet fiable con opciones de respaldo
                        </>,
                        <>Connexion internet fiable avec options de secours</>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            백업
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            옵션을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            갖춘
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            안정적인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            인터넷
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            연결
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>Offline playback capability for internet outages</>,
                        <>
                          Capacidad de reproducción sin conexión en caso de
                          interrupciones de internet
                        </>,
                        <>
                          Fonctionnalité de lecture hors ligne en cas de panne
                          d'internet
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            인터넷
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            연결이
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            끊겼을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            때
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            오프라인
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            재생
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            기능
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            제공
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                    <li className={classes.liArticle}>
                      {translate(
                        <>
                          Technical support availability for quick issue
                          resolution
                        </>,
                        <>
                          Disponibilidad de soporte técnico para la rápida
                          resolución de problemas
                        </>,
                        <>
                          Assistance technique disponible pour une résolution
                          rapide des problèmes
                        </>,
                        <>
                          <span
                            aria-hidden={true}
                            className={classes.asianTextBlock}
                          >
                            신속한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            문제
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            해결을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            위한
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            기술
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            지원을
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            이용할
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            수
                          </span>{' '}
                          <span
                            className={classes.asianTextBlock}
                            aria-hidden={true}
                          >
                            있습니다.
                          </span>
                        </>,
                        selectedLanguage
                      )}
                    </li>
                  </ul>
                </section>
              </section>
            </section>
            <section id={'concluding-remarks'}>
              <h2 className={classes.subSection}>
                {translate(
                  <>Final Thoughts</>,
                  <>Reflexiones finales</>,
                  <>Réflexions finales</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      마무리
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      생각
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id={'business-that-succeed-with-background-music'}>
                <h4 className={classes.pParagraph}>
                  {translate(
                    <>Businesses mastering commercial music selection:</>,
                    <>Empresas que dominan la selección de música comercial:</>,
                    <>
                      Les entreprises qui maîtrisent la sélection de musique
                      commerciale&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상업용
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        음악
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        선곡을
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        능숙하게
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        활용하는
                      </span>{' '}
                      <span
                        className={classes.asianTextBlock}
                        aria-hidden={true}
                      >
                        기업들:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h4>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Create memories that customers want to reexperience</>,
                      <>
                        Crea experiencias memorables que los clientes quieran
                        revivir
                      </>,
                      <>
                        Créez des expériences mémorables que les clients auront
                        envie de revivre
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고객이
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          다시
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          경험하고
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          싶어하는
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          추억을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          만들어주세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Build emotional brand connections</>,
                      <>Construir conexiones emocionales con la marca</>,
                      <>Établissez des liens émotionnels avec votre marque</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          감정적인
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          브랜드
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          연결을
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          구축하세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Differentiate from competitors</>,
                      <>Diferenciarse de la competencia</>,
                      <>Se différencier de la concurrence</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          경쟁업체와
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          차별화하세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Promote significant growth in dwell time and spending
                      </>,
                      <>
                        Promover un crecimiento significativo en el tiempo de
                        permanencia y el gasto
                      </>,
                      <>
                        Promouvoir une augmentation significative du temps passé
                        sur place et des dépenses
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          체류
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          시간과
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          지출액의
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          상당한
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          증가를
                        </span>{' '}
                        <span
                          className={classes.asianTextBlock}
                          aria-hidden={true}
                        >
                          촉진합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Stop making expensive mistakes. Start creating treasurable
                    experiences with strategic sound.
                  </>,
                  <>
                    Deja de cometer errores costosos. Empieza a crear
                    experiencias inolvidables con una estrategia sólida.
                  </>,
                  <>
                    Arrêtez de commettre des erreurs coûteuses. Commencez à
                    créer des expériences inoubliables grâce à une stratégie
                    judicieuse.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      값비싼
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      실수를
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      반복하지
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      마세요.
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      전략적인
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      판단을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      통해
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      소중한
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      경험을
                    </span>{' '}
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      만들어나가세요.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisorBottomOfArticle} />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
            <br />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <ReadyToTransform
              selectedLanguage={selectedLanguage}
              linkVariant={false}
            />
          </Grid.Col>
          <Grid.Col md={24}>
            <div>
              <br />
              <br />
              <br />
            </div>
          </Grid.Col>
        </Grid>
      </div>
    </div>
  )
}
